import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { WeezHeader, Tab, Message, ToastContext } from '@weezevent/nacre';
import { useFetchStatistics } from './components/statisticsFetcher';
import useAppContext from '../../context/useAppContext';
import ChannelsStats from './channelsStats';
import CrossSellingModel from '../../models/crossSellingModel';
import Style from './index.module.css';
import CrossSellingStats from './crossSellingStats';
import RatesStats from './ratesStats';
import CardsStats from './cardsStats';
import ResaleStatsCard from './resaleStats';
import Distributor from '../../models/distributor';
import i18n from 'i18next';
import RateModel from '../../models/rateModel';
import { usePermissions } from '../../user/permissions';

export default function AttendeesStats() {
    const { t } = useTranslation();
    const permissions = usePermissions();
    const [{ organization, event }] = useAppContext();
    const locale = i18n.language;
    const toast = React.useContext(ToastContext);

    const [activeTab, setActiveTab] = React.useState(0);
    const [stats, setStats] = React.useState(null);
    const [countRates, onChangeCountRates] = React.useState(0);

    const displayResaleStats = React.useMemo(() => {
        return permissions.hasCapacity('PyvarCapacity.STATS', organization.id) && event.has_resale_config;
    }, [permissions, organization, event]);

    const [distributors, loadingDistributors] = Distributor.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id
        },
        [organization, event]
    );

    const [, loadingRates] = RateModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            query: {
                limit: 1000
            },
            onSuccess: rates => {
                onChangeCountRates(rates.length);
            }
        },
        [organization, event]
    );

    const statisticsQueryParams = React.useMemo(() => {
        let query = ['sales_by_channel_stats', 'sales_by_rate_stats', 'sales_by_cross_sellings_stats'];

        if (displayResaleStats) {
            query.push('sales_by_resale_stats');
        }

        return query.join(',');
    }, [displayResaleStats]);

    const statistics = useFetchStatistics({//eslint-disable-line
        route: `/ticket/organizations/${organization.id}/events/${event.id}/stats`,
        queryParams: {
            stats: statisticsQueryParams
        },
        onStats: stats => {
            setStats(stats);
        },
        onError: () => toast.error(t('ticket.eventDashboard.statistics.error_retrieving_stats'))
    });

    const lastComputedDate = React.useMemo(() => {
        if (!stats || !Array.isArray(stats) || !stats.length) {
            return null;
        }

        return stats[0]?.last_computed;
    }, [stats]);

    const lastUpdateDate = React.useMemo(() => {
        if (!lastComputedDate) {
            return t('ticket.attendees_stats.header.lastUpdate', { date: t('ticket.attendees_stats.header.computeInProgress') });
        }

        const options = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: false
        };

        return t('ticket.attendees_stats.header.lastUpdate', {
            date: new Intl.DateTimeFormat(locale, options).format(new Date(lastComputedDate))
        });
    }, [stats, locale, lastComputedDate]);

    const [products] = CrossSellingModel.useApiModel(
        {
            organizationId: organization.id,
            query: {
                limit: 1
            }
        },
        [organization]
    );

    const renderTabs = React.useMemo(() => {
        let tabs = [
            {
                menuItem: t('ticket.attendees_stats.channels.tabTitle'),
                menuIcon: 'Piechart',
                render: () => <ChannelsStats stats={stats} distributors={distributors} loadingDistributors={loadingDistributors} />
            },
            {
                menuItem: t('ticket.attendees_stats.rates.tabTitle'),
                menuIcon: 'cart',
                render: () => <RatesStats stats={stats} />
            }
        ];
        if (displayResaleStats) {
            tabs.push({
                menuItem: t('ticket.attendees_stats.resale.tabTitle'),
                menuIcon: 'cart',
                render: () => <ResaleStatsCard stats={stats} />
            });
        }
        if (products?.length > 0) {
            tabs.push({
                menuItem: t('ticket.attendees_stats.cross_selling.tabTitle'),
                menuIcon: 'credit-card',
                render: () => <CrossSellingStats stats={stats} />
            });
        }
        return tabs;
    }, [displayResaleStats, stats, distributors, loadingDistributors, products]);

    return (
        <div className={Style['attendees_stats_container']}>
            <WeezHeader title={t('ticket.attendee_stats.title')}>{lastUpdateDate}</WeezHeader>
            {!lastComputedDate && (
                <Message type="default" title={t('ticket.attendees_stats.header.messageComputeInProgress.title')}>
                    {t('ticket.attendees_stats.header.messageComputeInProgress.content')}
                </Message>
            )}
            <CardsStats stats={stats} countRates={countRates} loadingRates={loadingRates} />
            <Tab
                panes={renderTabs}
                activeTab={activeTab}
                onTabSelected={(pane, index) => {
                    setActiveTab(index);
                }}
            />
        </div>
    );
}

AttendeesStats.propTypes = {
    menhirUrl: PropTypes.string.isRequired
};
